import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { Link } from "react-router-dom";
import {
  api,
  ApiError,
  AggregatePublicationDataValue,
  AggregatePublicationItem,
  AggregatePublicationPreview,
  AggregatePublicationProfile,
  AggregatePublicationRun
} from "../../lib/api.js";
import EmptyState from "../../components/EmptyState.js";
import Drawer from "../../components/Drawer.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";

type RunWithItems = AggregatePublicationRun & { items: AggregatePublicationItem[] };

function statusPill(status: AggregatePublicationRun["status"]) {
  switch (status) {
    case "completed": return <span className="pill pill-ok">Completed</span>;
    case "failed":    return <span className="pill pill-err">Failed</span>;
    case "running":   return <span className="pill pill-info">Running</span>;
    case "preview":   return <span className="pill pill-muted">Preview</span>;
    default:          return <span className="pill pill-muted">{status}</span>;
  }
}

export default function AdminPublications() {
  const [profiles, setProfiles] = useState<AggregatePublicationProfile[]>([]);
  const [runs, setRuns] = useState<AggregatePublicationRun[]>([]);
  const [items, setItems] = useState<AggregatePublicationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const [preview, setPreview] = useState<AggregatePublicationPreview | null>(null);
  const [previewProfileId, setPreviewProfileId] = useState<string | null>(null);
  const [openRun, setOpenRun] = useState<RunWithItems | null>(null);

  const refresh = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [p, r] = await Promise.all([
        api.aggregatePublicationProfiles(),
        api.aggregatePublicationRuns()
      ]);
      setProfiles(p.profiles ?? []);
      setRuns(r.runs ?? []);
      setItems(r.items ?? []);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  const profileById = useMemo(() => {
    const map = new Map<string, AggregatePublicationProfile>();
    for (const p of profiles) map.set(p.id, p);
    return map;
  }, [profiles]);

  const itemsByRun = useMemo(() => {
    const map = new Map<string, AggregatePublicationItem[]>();
    for (const i of items) {
      const list = map.get(i.publicationRunId) ?? [];
      list.push(i);
      map.set(i.publicationRunId, list);
    }
    return map;
  }, [items]);

  const previewIssuesRows = useMemo(() => preview?.issues ?? [], [preview]);
  const previewPayloadRows = useMemo(() => preview?.payload ?? [], [preview]);
  const openRunItemsRows = useMemo(() => openRun?.items ?? [], [openRun]);

  const t1 = useTableState(profiles as unknown as Array<Record<string, unknown>>, {
    searchFields: ["id", "name", "targetType", "endpoint"],
    initialPageSize: 25
  });
  const t2 = useTableState(runs as unknown as Array<Record<string, unknown>>, {
    searchFields: ["id", "profileId", "mode", "status", "startedAt"],
    initialPageSize: 25
  });
  const t3 = useTableState(previewIssuesRows as unknown as Array<Record<string, unknown>>, {
    searchFields: ["rollupFactId", "reason"],
    initialPageSize: 25
  });
  const t4 = useTableState(previewPayloadRows as unknown as Array<Record<string, unknown>>, {
    searchFields: ["dataElement", "period", "orgUnit", "categoryOptionCombo", "attributeOptionCombo", "value", "dataSet"],
    initialPageSize: 25
  });
  const t5 = useTableState(openRunItemsRows as unknown as Array<Record<string, unknown>>, {
    searchFields: ["status", "domainId", "rollupFactId", "message", "validationIssue"],
    initialPageSize: 25
  });

  const onPreview = async (profileId: string) => {
    setBusy(true);
    setErr(null);
    setBanner(null);
    try {
      const r = await api.previewAggregatePublication(profileId);
      setPreview(r.preview);
      setPreviewProfileId(profileId);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onPublish = async (profileId: string, dryRun = false) => {
    const profile = profileById.get(profileId);
    if (!profile) return;
    if (!dryRun && profile.targetType === "dhis2") {
      if (!confirm(`Publish to DHIS2 (${profile.endpoint})? This writes data values to the live system.`)) return;
    }
    setBusy(true);
    setErr(null);
    setBanner(null);
    try {
      const r = await api.runAggregatePublication(profileId, { dryRun });
      setBanner(`Run ${r.run.id} ${r.run.status}${r.run.importSummary ? ` · imported ${r.run.importSummary.imported}, updated ${r.run.importSummary.updated}, ignored ${r.run.importSummary.ignored}` : ""}.`);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onReplay = async (run: AggregatePublicationRun) => {
    if (!confirm(`Replay run ${run.id}? This re-sends the same payload.`)) return;
    setBusy(true);
    setErr(null);
    setBanner(null);
    try {
      const r = await api.replayAggregatePublication(run.id);
      setBanner(`Replay completed as ${r.run.id} (${r.run.status}).`);
      await refresh();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Publications</h1>
          <p>Preview, publish, and replay aggregate-domain rollups to DHIS2 and other targets.</p>
        </div>
        <div className="page-head-actions">
          <button type="button" className="btn" onClick={() => void refresh()} disabled={loading}>Refresh</button>
        </div>
      </header>

      {err && <div className="alert alert-error">{err}</div>}
      {banner && <div className="alert alert-info">{banner}</div>}

      <section className="section">
        <div className="section-head"><h2>Profiles ({profiles.length})</h2></div>
        {loading ? (
          <SkeletonTable rows={5} columns={4} />
        ) : profiles.length === 0 ? (
          <EmptyState
            title="No publication profiles"
            description={<>Define profiles in the <Link to="/admin/aggregate-domains">Aggregate Domain Studio</Link> to enable publication.</>}
          />
        ) : (
          <div className="table-scroll">
            <TableToolbar
            hideExport
              search={t1.search}
              setSearch={t1.setSearch}
              page={t1.page}
              setPage={t1.setPage}
              pageSize={t1.pageSize}
              setPageSize={t1.setPageSize}
              totalPages={t1.totalPages}
              totalRows={t1.totalRows}
            />
            <table className="data-table">
              <thead>
                <tr>
                  <th>Profile</th>
                  <th>Target</th>
                  <th>Endpoint</th>
                  <th>Domains</th>
                  <th>Enabled</th>
                  <th style={{ width: 320 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(t1.paged as unknown as AggregatePublicationProfile[]).map((p) => (
                  <tr key={p.id}>
                    <td><strong>{p.name}</strong><div className="soft" style={{ fontSize: 12 }}>{p.id}</div></td>
                    <td><span className={`pill ${p.targetType === "dhis2" ? "pill-ok" : "pill-info"}`}>{p.targetType}</span></td>
                    <td className="soft" style={{ maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis" }}>{p.endpoint}</td>
                    <td>{p.domainIds.length}</td>
                    <td>{p.enabled ? <span className="pill pill-ok">On</span> : <span className="pill pill-muted">Off</span>}</td>
                    <td>
                      <button type="button" className="btn" disabled={busy} onClick={() => void onPreview(p.id)}>Preview</button>
                      {" "}
                      <button type="button" className="btn" disabled={busy || !p.enabled} onClick={() => void onPublish(p.id, true)}>Dry run</button>
                      {" "}
                      <button type="button" className="btn btn-primary" disabled={busy || !p.enabled} onClick={() => void onPublish(p.id, false)}>Publish</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="section">
        <div className="section-head">
          <h2>Run history ({runs.length})</h2>
        </div>
        {runs.length === 0 ? (
          <div className="empty">No publication runs yet.</div>
        ) : (
          <div className="table-scroll">
            <TableToolbar
            hideExport
              search={t2.search}
              setSearch={t2.setSearch}
              page={t2.page}
              setPage={t2.setPage}
              pageSize={t2.pageSize}
              setPageSize={t2.setPageSize}
              totalPages={t2.totalPages}
              totalRows={t2.totalRows}
            />
            <table className="data-table">
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Profile</th>
                  <th>Mode</th>
                  <th>Status</th>
                  <th>Items</th>
                  <th>Import summary</th>
                  <th>Started</th>
                  <th style={{ width: 220 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(t2.paged as unknown as AggregatePublicationRun[]).map((run) => {
                  const profile = profileById.get(run.profileId);
                  const sum = run.importSummary;
                  return (
                    <tr key={run.id}>
                      <td><code>{run.id}</code></td>
                      <td>{profile?.name ?? run.profileId}</td>
                      <td className="soft">{run.mode ?? "—"}</td>
                      <td>{statusPill(run.status)}</td>
                      <td>{run.itemCount}</td>
                      <td className="soft">
                        {sum
                          ? `imp ${sum.imported} · upd ${sum.updated} · ign ${sum.ignored}${sum.conflicts.length ? ` · conflicts ${sum.conflicts.length}` : ""}`
                          : run.error
                            ? <span style={{ color: "#b91c1c" }}>{run.error.slice(0, 80)}</span>
                            : "—"}
                      </td>
                      <td className="soft">{new Date(run.startedAt).toLocaleString()}</td>
                      <td>
                        <button type="button" className="linkbtn" onClick={() => setOpenRun({ ...run, items: itemsByRun.get(run.id) ?? [] })}>View</button>
                        {" · "}
                        <button type="button" className="linkbtn" disabled={busy} onClick={() => void onReplay(run)}>Replay</button>
                        {profile && (
                          <>
                            {" · "}
                            <button type="button" className="linkbtn" disabled={busy || !profile.enabled} onClick={() => void onPublish(profile.id, false)}>Rebuild &amp; republish</button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Drawer
        open={Boolean(preview)}
        title={preview ? `Preview · ${profileById.get(preview.profileId)?.name ?? preview.profileId}` : ""}
        subtitle={preview ? `${preview.validCount} valid · ${preview.invalidCount} issues · ${preview.totalCandidates} candidates` : undefined}
        onClose={() => { setPreview(null); setPreviewProfileId(null); }}
        width={720}
        footer={
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="btn" onClick={() => { setPreview(null); setPreviewProfileId(null); }}>Close</button>
            {previewProfileId && (
              <>
                <button
                  type="button"
                  className="btn"
                  disabled={busy || !preview || preview.validCount === 0}
                  onClick={() => { void onPublish(previewProfileId, true); setPreview(null); }}
                >
                  Dry run
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={busy || !preview || preview.validCount === 0}
                  onClick={() => { void onPublish(previewProfileId, false); setPreview(null); }}
                >
                  Publish
                </button>
              </>
            )}
          </div>
        }
      >
        {preview && (
          <>
            <h3>Validation issues ({preview.issues.length})</h3>
            {preview.issues.length === 0 ? (
              <p className="soft">No validation issues.</p>
            ) : (
              <>
                <TableToolbar
            hideExport
                  search={t3.search}
                  setSearch={t3.setSearch}
                  page={t3.page}
                  setPage={t3.setPage}
                  pageSize={t3.pageSize}
                  setPageSize={t3.setPageSize}
                  totalPages={t3.totalPages}
                  totalRows={t3.totalRows}
                />
                <table className="data-table">
                  <thead><tr><th>Rollup fact</th><th>Reason</th></tr></thead>
                  <tbody>
                    {(t3.paged as unknown as Array<{ rollupFactId: string; reason: string }>).map((i, ix) => (
                      <tr key={`${i.rollupFactId}-${ix}`}>
                        <td><code>{i.rollupFactId.slice(0, 14)}…</code></td>
                        <td className="soft">{i.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            <h3 style={{ marginTop: 16 }}>Payload preview ({preview.payload.length})</h3>
            {preview.payload.length === 0 ? (
              <p className="soft">No data values to publish.</p>
            ) : (
              <>
                <TableToolbar
            hideExport
                  search={t4.search}
                  setSearch={t4.setSearch}
                  page={t4.page}
                  setPage={t4.setPage}
                  pageSize={t4.pageSize}
                  setPageSize={t4.setPageSize}
                  totalPages={t4.totalPages}
                  totalRows={t4.totalRows}
                />
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Data element</th><th>Period</th><th>Org unit</th><th>COC</th><th>AOC</th><th>Value</th><th>DataSet</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(t4.paged as unknown as AggregatePublicationDataValue[]).map((dv, ix) => (
                      <tr key={`${dv.dataElement}-${dv.period}-${dv.orgUnit}-${ix}`}>
                        <td><code>{dv.dataElement}</code></td>
                        <td>{dv.period}</td>
                        <td>{dv.orgUnit}</td>
                        <td className="soft">{dv.categoryOptionCombo}</td>
                        <td className="soft">{dv.attributeOptionCombo}</td>
                        <td style={{ textAlign: "right" }}>{dv.value}</td>
                        <td className="soft">{dv.dataSet ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </>
        )}
      </Drawer>

      <Drawer
        open={Boolean(openRun)}
        title={openRun ? `Run · ${openRun.id}` : ""}
        subtitle={openRun ? `${profileById.get(openRun.profileId)?.name ?? openRun.profileId} · ${openRun.status}` : undefined}
        onClose={() => setOpenRun(null)}
        width={720}
        footer={<button type="button" className="btn" onClick={() => setOpenRun(null)}>Close</button>}
      >
        {openRun && (
          <>
            <div className="stat-grid">
              <div className="stat"><div className="stat-label">Items</div><div className="stat-value">{openRun.itemCount}</div></div>
              {openRun.importSummary && (
                <>
                  <div className="stat"><div className="stat-label">Imported</div><div className="stat-value">{openRun.importSummary.imported}</div></div>
                  <div className="stat"><div className="stat-label">Updated</div><div className="stat-value">{openRun.importSummary.updated}</div></div>
                  <div className="stat"><div className="stat-label">Ignored</div><div className="stat-value">{openRun.importSummary.ignored}</div></div>
                  <div className="stat"><div className="stat-label">Conflicts</div><div className="stat-value" style={{ color: openRun.importSummary.conflicts.length > 0 ? "#b91c1c" : undefined }}>{openRun.importSummary.conflicts.length}</div></div>
                </>
              )}
            </div>
            {openRun.idempotencyKey && (
              <p className="soft" style={{ marginTop: 12 }}>Idempotency key: <code>{openRun.idempotencyKey}</code></p>
            )}
            {openRun.error && <div className="alert alert-error">{openRun.error}</div>}

            {openRun.importSummary && openRun.importSummary.conflicts.length > 0 && (
              <>
                <h3>Conflicts ({openRun.importSummary.conflicts.length})</h3>
                <table className="data-table">
                  <thead><tr><th>Object</th><th>Message</th></tr></thead>
                  <tbody>
                    {openRun.importSummary.conflicts.map((c, ix) => (
                      <tr key={ix}><td className="soft">{c.object ?? "—"}</td><td className="soft">{c.value}</td></tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            <h3 style={{ marginTop: 16 }}>Items ({openRun.items.length})</h3>
            {openRun.items.length === 0 ? (
              <p className="soft">No items recorded for this run.</p>
            ) : (
              <>
                <TableToolbar
            hideExport
                  search={t5.search}
                  setSearch={t5.setSearch}
                  page={t5.page}
                  setPage={t5.setPage}
                  pageSize={t5.pageSize}
                  setPageSize={t5.setPageSize}
                  totalPages={t5.totalPages}
                  totalRows={t5.totalRows}
                />
                <table className="data-table">
                  <thead>
                    <tr><th>Status</th><th>Domain</th><th>Rollup fact</th><th>Data value</th><th>Note</th></tr>
                  </thead>
                  <tbody>
                    {(t5.paged as unknown as AggregatePublicationItem[]).map((item) => (
                    <tr key={item.id}>
                      <td>
                        <span className={`pill ${
                          item.status === "published" ? "pill-ok"
                          : item.status === "failed" ? "pill-err"
                          : item.status === "skipped" ? "pill-muted"
                          : "pill-info"
                        }`}>{item.status}</span>
                      </td>
                      <td className="soft">{item.domainId}</td>
                      <td><code>{item.rollupFactId.slice(0, 14)}…</code></td>
                      <td className="soft">
                        {item.dataValue
                          ? `${item.dataValue.dataElement} / ${item.dataValue.period} / ${item.dataValue.orgUnit} = ${item.dataValue.value}`
                          : "—"}
                      </td>
                      <td className="soft">{item.validationIssue ?? item.message ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
                </table>
              </>
            )}
            {openRun.items.length > 200 && <p className="soft">Showing first 200 of {openRun.items.length} items.</p>}
          </>
        )}
      </Drawer>
    </div>
  );
}
